import { useEffect, useRef, useState } from 'react';

/*
  Drives the desktop navigation menu expansion with JS events instead of CSS
  :hover. Tailwind v4 gates hover: variants behind hover-capability media
  queries, which some hybrid touch devices (e.g. Surface Pro) misreport even
  when a mouse is attached.

  Mouse input: hover to expand, click to navigate, as before.
  Touch/pen input: two-tap pattern — the first tap on the collapsed menu only
  expands it (navigation is intercepted); the next tap navigates.

  The intercept decision is snapshotted on pointerdown because browsers fire
  synthetic mouseenter (which expands the menu) between the tap and its click
  event — by click time the menu already looks expanded.
*/
export function useMenuExpansion() {
  const [isMenuExpanded, setIsMenuExpanded] = useState(false);
  const isExpandedRef = useRef(false);
  const tapOnCollapsedMenuRef = useRef(false);

  const setExpanded = (value: boolean) => {
    isExpandedRef.current = value;
    setIsMenuExpanded(value);
  };

  useEffect(() => {
    const handlePointerDown = (event: PointerEvent) => {
      tapOnCollapsedMenuRef.current =
        event.pointerType !== 'mouse' && !isExpandedRef.current;
    };
    window.addEventListener('pointerdown', handlePointerDown, true);
    return () => window.removeEventListener('pointerdown', handlePointerDown, true);
  }, []);

  const shouldInterceptNavigation = () => {
    const shouldIntercept = tapOnCollapsedMenuRef.current;
    tapOnCollapsedMenuRef.current = false;
    if (shouldIntercept) setExpanded(true);
    return shouldIntercept;
  };

  return {
    isMenuExpanded,
    menuHoverProps: {
      onMouseEnter: () => setExpanded(true),
      onMouseLeave: () => setExpanded(false),
    },
    shouldInterceptNavigation,
  };
}
